// frontend/src/components/cardOnFile/cardOnFileView.js

import { formatDiscountPercent } from "../upgradeOfferText";

/**
 * The display model of the `card_on_file` block of `GET /payments/offer`.
 *
 * READ, NEVER FETCHED. The block rides the one offer read `useUpgradeOffer`
 * already shares between the navbar, the dashboard banner and the checkout, so
 * this module is a pure function over that payload - no second request for the
 * card-on-file surfaces, ever.
 *
 * DISPLAY == CHARGE, AND == CONSENT. Every number here is the server's own: the
 * per-plan `amount` / `full` come from the pricer the consent row and the first
 * charge use, and the consent label (W6) is a server-rendered string the page
 * shows verbatim. Nothing here recomputes a price, a percent or a date; the
 * client only FORMATS them, with the same rule the server's `format_money`
 * uses so the price lines and the consent label print the same digits.
 *
 * FAIL-CLOSED. A missing block, `can_save: false`, a live schedule, a
 * malformed plan, or a plan with no discount all yield `offerable: false` (or
 * drop that plan), and every surface then renders exactly what it rendered
 * before the feature existed.
 *
 * TWO LEVELS (round 2). `can_save` - the save may be OFFERED (the payment
 * page's choice, from trial day 1); `promote` - the server also wants it
 * PUSHED (the dashboard banner and the strip's save-card state). `promote`
 * implies `can_save`. A payload from before round 2 carries neither and is
 * read through `eligible`, which the server keeps equal to `promote` - so an
 * old payload offers and promotes exactly where it used to.
 */

/** The block's `reason` while `CARD_ON_FILE_ENABLED` is off (server token). */
export const CARD_ON_FILE_REASON_DISABLED = "disabled";

/** The self-serve plans a card can be saved for (`CardOnFilePlan`). */
export const CARD_ON_FILE_PLANS = Object.freeze(["solo", "professional"]);

/**
 * The plan the dashboard banner quotes - the one the existing offer banner
 * already features (its saving is the Professional plan's), falling back to
 * Solo when only Solo can be quoted.
 */
export const BANNER_PLAN_ORDER = Object.freeze(["professional", "solo"]);

/**
 * `/payment?choice=save-card` - the banner's button lands here with the
 * "save card, pay at the end" choice preselected on every plan card.
 */
export const CARD_ON_FILE_CHOICE_PARAM = "choice";
export const CARD_ON_FILE_CHOICE_VALUE = "save-card";
export const CARD_ON_FILE_PAYMENT_PATH = `/payment?${CARD_ON_FILE_CHOICE_PARAM}=${CARD_ON_FILE_CHOICE_VALUE}`;

/** The two choices on a plan card. */
export const CHOICE = Object.freeze({ PAY_NOW: "pay_now", SAVE_CARD: "save_card" });

/**
 * Analytics `surface` values for the existing consent-gated
 * `upgrade_offer_shown / _cta / _dismissed` events. The backend schema takes
 * any surface string (no allowlist), so these need no schema change.
 */
export const CARD_ON_FILE_BANNER_SURFACE = "card_on_file_banner";
export const CARD_ON_FILE_PAYMENT_SURFACE = "card_on_file_payment";
/** The dashboard's permanent trial strip (`TrialOfferStrip`); its events also
 *  carry the strip `state` (pay_now / save_card / ending / last_day). */
export const TRIAL_OFFER_STRIP_SURFACE = "strip";

const MONEY_RE = /^\d+(\.\d+)?$/;
const DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * The amount as the request must echo it: the server's own decimal, at most
 * two places (the request field is `decimal_places=2`), trailing zeros
 * dropped. Null for anything that is not a plain non-negative decimal - such a
 * plan is not offered at all.
 *
 * @param {unknown} raw  a JSON decimal (pydantic sends a string) or a number
 * @returns {string|null}
 */
export const toWireAmount = (raw) => {
  const text = typeof raw === "number" && Number.isFinite(raw) ? String(raw) : raw;
  if (typeof text !== "string" || !MONEY_RE.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  const trimmed = fraction.replace(/0+$/, "");
  if (trimmed.length > 2) return null;
  const normalizedWhole = whole.replace(/^0+(?=\d)/, "");
  return trimmed ? `${normalizedWhole}.${trimmed}` : normalizedWhole;
};

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * The server's `format_money`, mirrored: whole shekels drop the agorot
 * ("1,500"), anything else keeps exactly two places ("1,234.50"). The consent
 * label is rendered with that function, so the price lines above it must print
 * the same digits. Null for a value `toWireAmount` refuses.
 *
 * @param {unknown} raw
 * @returns {string|null}
 */
export const formatMoney = (raw) => {
  const wire = toWireAmount(raw);
  if (wire === null) return null;
  const [whole, fraction = ""] = wire.split(".");
  const grouped = groupThousands(whole);
  return fraction ? `${grouped}.${fraction.padEnd(2, "0")}` : grouped;
};

/**
 * One plan's quote, or null when it cannot be shown truthfully.
 *
 * Requires: both amounts valid and positive, a first charge strictly below the
 * full price (the feature's whole promise is "lock the discount" - W1 - so a
 * plan with nothing to lock is not offered), a known discount kind, and a
 * non-empty server consent label.
 */
const readPlan = (planKey, raw) => {
  if (!raw || typeof raw !== "object") return null;
  const amountWire = toWireAmount(raw.amount);
  const fullWire = toWireAmount(raw.full);
  if (amountWire === null || fullWire === null) return null;
  const amount = Number(amountWire);
  const full = Number(fullWire);
  if (!(amount > 0) || !(full > amount)) return null;
  if (raw.discount_kind !== "early_upgrade" && raw.discount_kind !== "referral") return null;
  if (typeof raw.consent_label !== "string" || !raw.consent_label.trim()) return null;
  const percent = Number(raw.percent);
  // Tomorrow's first-month price (round 2); shown only when it is a real,
  // positive amount still below the full price.
  const nextWire = toWireAmount(raw.next_amount);
  const nextAmount = nextWire === null ? NaN : Number(nextWire);
  return {
    plan: planKey,
    amountWire,
    amountText: formatMoney(amountWire),
    fullText: formatMoney(fullWire),
    nextAmountText: nextAmount > 0 && nextAmount < full ? formatMoney(nextWire) : null,
    percent: Number.isFinite(percent) && percent > 0 ? percent : null,
    percentText: Number.isFinite(percent) && percent > 0 ? formatDiscountPercent(percent) : null,
    isReferral: raw.discount_kind === "referral",
    discountKind: raw.discount_kind,
    consentLabel: raw.consent_label,
  };
};

const NOT_OFFERED = Object.freeze({
  offerable: false,
  promote: false,
  scheduled: false,
  firstChargeFailed: false,
  firstChargeDate: null,
  termsVersion: null,
  plans: Object.freeze({}),
});

/**
 * @param {object|null|undefined} offer  the `GET /payments/offer` payload
 * @returns {{
 *   offerable: boolean,
 *   promote: boolean,
 *   scheduled: boolean,
 *   firstChargeFailed: boolean,
 *   firstChargeDate: string|null,
 *   termsVersion: string|null,
 *   plans: Object<string, object>,
 * }}
 *   `offerable` - a NEW save may be offered (`can_save`, no live schedule, at
 *   least one plan quotable). `promote` - offerable AND the server promotes
 *   it (banner, strip). `scheduled` - the caller already holds a
 *   scheduled first charge (whatever `eligible` says): the dashboard shows no
 *   upgrade banner at all for them. `firstChargeFailed` - that scheduled
 *   first charge already failed (the server's `scheduled.first_charge_failed`):
 *   the user is in its grace days and the dashboard asks for a new payment
 *   method instead.
 */
export const resolveCardOnFileView = (offer) => {
  const block = offer?.card_on_file;
  if (!block || typeof block !== "object") return NOT_OFFERED;
  const scheduled = Boolean(block.scheduled && typeof block.scheduled === "object");
  if (scheduled) {
    return {
      ...NOT_OFFERED,
      scheduled: true,
      firstChargeFailed: block.scheduled.first_charge_failed === true,
    };
  }
  const legacyEligible = block.eligible === true;
  const canSave = typeof block.can_save === "boolean" ? block.can_save : legacyEligible;
  const promote = typeof block.promote === "boolean" ? block.promote : legacyEligible;
  if (!canSave) return NOT_OFFERED;
  if (typeof block.first_charge_date !== "string" || !DATE_RE.test(block.first_charge_date)) {
    return NOT_OFFERED;
  }
  if (typeof block.terms_version !== "string" || !block.terms_version) return NOT_OFFERED;

  const plans = {};
  CARD_ON_FILE_PLANS.forEach((planKey) => {
    const quote = readPlan(planKey, block.plans?.[planKey]);
    if (quote) plans[planKey] = quote;
  });
  if (Object.keys(plans).length === 0) return NOT_OFFERED;

  return {
    offerable: true,
    promote,
    scheduled: false,
    firstChargeFailed: false,
    firstChargeDate: block.first_charge_date,
    termsVersion: block.terms_version,
    plans,
  };
};

/**
 * Is the card-on-file feature live for this payload - a block the server built
 * with `CARD_ON_FILE_ENABLED` on? Every round-2 surface (the strip, the
 * drops-daily line) is gated on it: their copy is still placeholders, and an
 * off flag must leave production exactly as it was.
 *
 * @param {object|null|undefined} offer  the `GET /payments/offer` payload
 * @returns {boolean}
 */
export const isCardOnFileLive = (offer) => {
  const block = offer?.card_on_file;
  return Boolean(
    block &&
      typeof block === "object" &&
      typeof block.reason === "string" &&
      block.reason !== CARD_ON_FILE_REASON_DISABLED
  );
};

/**
 * The plan quote the dashboard banner shows (see `BANNER_PLAN_ORDER`), or null.
 *
 * @param {ReturnType<typeof resolveCardOnFileView>} view
 */
export const bannerPlanQuote = (view) => {
  if (!view?.offerable) return null;
  const key = BANNER_PLAN_ORDER.find((planKey) => view.plans[planKey]);
  return key ? view.plans[key] : null;
};

/**
 * Was the payment page opened from the banner's button (choice preselected)?
 * Read once on mount; any failure answers no.
 *
 * @param {string} [search]  defaults to `window.location.search`
 * @returns {boolean}
 */
export const readPreselectedSaveCard = (search) => {
  try {
    const query = search ?? window.location.search;
    return new URLSearchParams(query).get(CARD_ON_FILE_CHOICE_PARAM) === CARD_ON_FILE_CHOICE_VALUE;
  } catch (error) {
    return false;
  }
};
